import React, { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './AutoMode.css';

const AutoMode = ({ user }) => {
  const [autoModeSettings, setAutoModeSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('twitter');

  // Server URL - use same pattern as VideoGeneratorRemote
  const SERVER_URL = '/api/video';

  useEffect(() => {
    // Subscribe to user's auto mode settings in real-time
    const unsubscribe = onSnapshot(
      doc(db, 'users', user.uid, 'settings', 'autoMode'),
      (doc) => {
        if (doc.exists()) {
          const settings = doc.data();
          setAutoModeSettings(settings);
          setUsername(settings.username || '');
        } else {
          setAutoModeSettings(null);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to auto mode settings:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user.uid]);

  const handleSetupAutoMode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      // Validate username
      const cleanUsername = username.replace('@', '').trim();
      if (!cleanUsername) {
        throw new Error('Please enter a valid Twitter username');
      }

      // Save auto mode settings via API
      const response = await fetch(`${SERVER_URL}/auto-mode/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: user.uid,
          username: cleanUsername,
          enabled: true
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to setup Auto Mode');
      }

      setSuccess('Auto Mode configured successfully! Videos will be generated automatically for new tweets.');
      setUsername(cleanUsername);

    } catch (error) {
      console.error('Error setting up Auto Mode:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAutoMode = async (enabled) => {
    setError('');
    setSaving(true);

    try {
      const response = await fetch(`${SERVER_URL}/auto-mode/toggle/${user.uid}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to toggle Auto Mode');
      }

      setSuccess(enabled ? 'Auto Mode enabled!' : 'Auto Mode disabled');

    } catch (error) {
      console.error('Error toggling Auto Mode:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="autopost-factory">
        <div className="factory-content">
          <div className="system-loading">
            <div className="loading-animation">
              <div className="concentric-ring ring-1"></div>
              <div className="concentric-ring ring-2"></div>
              <div className="concentric-ring ring-3"></div>
              <div className="loading-center-dot"></div>
            </div>
            <div className="loading-text">
              <span className="terminal-prefix">System</span>
              <span className="loading-message">Loading AutoMode settings...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="autopost-factory">
      <div className="factory-content">
        {/* Industrial Header */}
        <div className="factory-header">
          <div className="header-grid">
            <div className="system-status">
              <div className="status-indicator active"></div>
              <span className="system-label">AUTOMODE SYSTEM</span>
            </div>
            <div className="factory-logo">
              <div className="logo-grid">
                <div className="grid-dot"></div>
                <div className="grid-dot"></div>
                <div className="grid-dot"></div>
                <div className="grid-dot"></div>
              </div>
            </div>
          </div>
          <h1 className="factory-title">
            <span className="title-line">AUTO-POSTING</span>
          </h1>
          <p className="factory-description">
            Automated video generation and posting for your social media content
          </p>
        </div>

        {/* System Alerts */}
        {error && (
          <div className="system-alert alert-error">
            <div className="alert-indicator"></div>
            <div className="alert-content">
              <span className="alert-code">ERROR:</span>
              <span className="alert-message">{error}</span>
            </div>
          </div>
        )}

        {success && (
          <div className="system-alert alert-success">
            <div className="alert-indicator"></div>
            <div className="alert-content">
              <span className="alert-code">SUCCESS:</span>
              <span className="alert-message">{success}</span>
            </div>
          </div>
        )}

        {/* Platform Selection */}
        <div className="platform-selector">
          <h2 className="section-title">
            PLATFORMS
          </h2>
          <div className="platform-grid">
            <div 
              className={`platform-module ${selectedPlatform === 'twitter' ? 'active' : ''}`}
              onClick={() => setSelectedPlatform('twitter')}
            >
              <div className="platform-header">
                <div className="platform-status operational"></div>
                <span className="platform-name">Twitter</span>
                <div className="platform-badge active">Available</div>
              </div>
              <div className="platform-metrics">
                <div className="metric">
                                      <span className="metric-label">Status:</span>
                    <span className="metric-value">Ready</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">API:</span>
                    <span className="metric-value">Connected</span>
                </div>
              </div>
            </div>

            <div 
              className={`platform-module ${selectedPlatform === 'instagram' ? 'active' : ''} disabled`}
              onClick={() => setSelectedPlatform('instagram')}
            >
              <div className="platform-header">
                <div className="platform-status pending"></div>
                <span className="platform-name">Instagram</span>
                <div className="platform-badge coming-soon">Coming Soon</div>
              </div>
              <div className="platform-metrics">
                <div className="metric">
                  <span className="metric-label">Status:</span>
                  <span className="metric-value">In Development</span>
                </div>
                <div className="metric">
                  <span className="metric-label">API:</span>
                  <span className="metric-value">Development</span>
                </div>
              </div>
            </div>

            <div 
              className={`platform-module ${selectedPlatform === 'tiktok' ? 'active' : ''} disabled`}
              onClick={() => setSelectedPlatform('tiktok')}
            >
              <div className="platform-header">
                <div className="platform-status pending"></div>
                <span className="platform-name">TikTok</span>
                <div className="platform-badge coming-soon">Coming Soon</div>
              </div>
              <div className="platform-metrics">
                <div className="metric">
                  <span className="metric-label">Status:</span>
                  <span className="metric-value">In Development</span>
                </div>
                <div className="metric">
                  <span className="metric-label">API:</span>
                  <span className="metric-value">Development</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Twitter Configuration */}
        {selectedPlatform === 'twitter' && (
          <div className="configuration-panel">
            {!autoModeSettings ? (
              <div className="setup-module">
                <div className="module-header">
                  <h3 className="module-title">
                    Twitter Configuration
                  </h3>
                  <div className="module-status">
                    <span className="status-label">Setup Required</span>
                  </div>
                </div>

                <form onSubmit={handleSetupAutoMode} className="config-form">
                  <div className="input-module">
                    <label className="input-label">
                      <span className="label-code">Twitter Username:</span>
                    </label>
                    <div className="input-container">
                      <span className="input-prefix">@</span>
                      <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="enter_username"
                        disabled={saving}
                        className="industrial-input"
                        required
                      />
                      <div className="input-status"></div>
                    </div>
                    <div className="input-help">
                      Enter the Twitter username to monitor for new tweets (exclude @ symbol)
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={saving || !username.trim()}
                    className="deploy-button"
                  >
                    <div className="button-content">
                      {saving ? (
                        <>
                          <div className="button-spinner"></div>
                          <span>Setting up...</span>
                        </>
                      ) : (
                        <>
                          <div className="button-icon">⚡</div>
                          <span>Enable AutoMode</span>
                        </>
                      )}
                    </div>
                  </button>
                </form>

                <div className="system-specs">
                  <h4 className="specs-title">Features</h4>
                  <div className="specs-grid">
                    <div className="spec-item">
                      <div className="spec-icon">⏰</div>
                      <div className="spec-content">
                        <div className="spec-name">Monitoring Frequency</div>
                        <div className="spec-value">Hourly checks</div>
                      </div>
                    </div>
                    <div className="spec-item">
                      <div className="spec-icon">🎬</div>
                      <div className="spec-content">
                        <div className="spec-name">Video Generation</div>
                        <div className="spec-value">Automatic processing</div>
                      </div>
                    </div>
                    <div className="spec-item">
                      <div className="spec-icon">☁️</div>
                      <div className="spec-content">
                        <div className="spec-name">Cloud Storage</div>
                        <div className="spec-value">Auto-upload</div>
                      </div>
                    </div>
                    <div className="spec-item">
                      <div className="spec-icon">🔄</div>
                      <div className="spec-content">
                        <div className="spec-name">Duplicate Handling</div>
                        <div className="spec-value">Smart filtering</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="control-module">
                <div className="module-header">
                  <h3 className="module-title">
                    Twitter Control Panel
                  </h3>
                  <div className="module-status">
                    <span className={`status-label ${autoModeSettings.enabled ? 'active' : 'standby'}`}>
                      {autoModeSettings.enabled ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="control-interface">
                  <div className="target-info">
                    <div className="info-label">Target:</div>
                    <div className="info-value">@{autoModeSettings.username}</div>
                    <div className="system-switch">
                      <button
                        onClick={() => handleToggleAutoMode(!autoModeSettings.enabled)}
                        disabled={saving}
                        className={`power-switch ${autoModeSettings.enabled ? 'on' : 'off'}`}
                      >
                        <div className="switch-indicator"></div>
                        <span className="switch-label">
                          {autoModeSettings.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="metrics-grid">
                  <div className="metric-module">
                    <div className="metric-header">
                      <div className="metric-icon">📊</div>
                      <span className="metric-title">Processed</span>
                    </div>
                    <div className="metric-display">
                      <span className="metric-number">
                        {autoModeSettings.lastProcessedTweetId ? '1+' : '0'}
                      </span>
                      <span className="metric-unit">tweets</span>
                    </div>
                  </div>

                  <div className="metric-module">
                    <div className="metric-header">
                      <div className="metric-icon">⏱️</div>
                      <span className="metric-title">Last Check</span>
                    </div>
                    <div className="metric-display">
                      <span className="metric-time">
                        {formatDate(autoModeSettings.lastProcessedAt)}
                      </span>
                    </div>
                  </div>

                  <div className="metric-module">
                    <div className="metric-header">
                      <div className="metric-icon">🎯</div>
                      <span className="metric-title">Target</span>
                    </div>
                    <div className="metric-display">
                      <span className="metric-target">@{autoModeSettings.username}</span>
                    </div>
                  </div>
                </div>

                <div className="operational-info">
                  <h4 className="info-title">How it works</h4>
                  <div className="info-grid">
                    <div className="info-module">
                      <div className="info-header">
                        <div className="info-icon">🕐</div>
                        <span className="info-label">Monitoring Schedule</span>
                      </div>
                      <p className="info-description">
                        The system checks for new tweets every hour and automatically generates videos for any new content found.
                      </p>
                    </div>

                    <div className="info-module">
                      <div className="info-header">
                        <div className="info-icon">🎬</div>
                        <span className="info-label">Video Processing</span>
                      </div>
                      <p className="info-description">
                        Each new tweet is automatically converted into a video using your default settings and audio track.
                      </p>
                    </div>

                    <div className="info-module">
                      <div className="info-header">
                        <div className="info-icon">📝</div>
                        <span className="info-label">Smart Processing</span>
                      </div>
                      <p className="info-description">
                        The system intelligently processes multiple tweets and avoids creating duplicate videos for the same content.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Instagram Placeholder */}
        {selectedPlatform === 'instagram' && (
          <div className="placeholder-module">
            <div className="placeholder-header">
              <h3 className="module-title">
                Instagram Integration
              </h3>
              <div className="development-badge">IN DEVELOPMENT</div>
            </div>
            
            <div className="placeholder-content">
              <div className="placeholder-visual">
                <div className="construction-grid">
                  <div className="grid-line"></div>
                  <div className="grid-line"></div>
                  <div className="grid-line"></div>
                  <div className="grid-line"></div>
                </div>
                <div className="construction-icon">🏗️</div>
              </div>
              
              <div className="placeholder-info">
                <h4>Instagram AutoMode</h4>
                <p>Automated posting to Instagram Stories & Reels is currently in development.</p>
                
                <div className="planned-features">
                  <div className="feature-preview">
                    <span className="feature-bullet">▸</span>
                    <span>Automated Story posting</span>
                  </div>
                  <div className="feature-preview">
                    <span className="feature-bullet">▸</span>
                    <span>Reels distribution</span>
                  </div>
                  <div className="feature-preview">
                    <span className="feature-bullet">▸</span>
                    <span>Hashtag optimization</span>
                  </div>
                  <div className="feature-preview">
                    <span className="feature-bullet">▸</span>
                    <span>Engagement tracking</span>
                  </div>
                </div>
                
                <div className="eta-info">
                  <span className="eta-label">ETA:</span>
                  <span className="eta-value">Q2 2024</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TikTok Placeholder */}
        {selectedPlatform === 'tiktok' && (
          <div className="placeholder-module">
            <div className="placeholder-header">
              <h3 className="module-title">
                TikTok Integration
              </h3>
              <div className="development-badge">IN DEVELOPMENT</div>
            </div>
            
            <div className="placeholder-content">
              <div className="placeholder-visual">
                <div className="construction-grid">
                  <div className="grid-line"></div>
                  <div className="grid-line"></div>
                  <div className="grid-line"></div>
                  <div className="grid-line"></div>
                </div>
                <div className="construction-icon">⚡</div>
              </div>
              
              <div className="placeholder-info">
                <h4>TikTok AutoMode</h4>
                <p>Automated posting to TikTok with intelligent optimization for maximum engagement.</p>
                
                <div className="planned-features">
                  <div className="feature-preview">
                    <span className="feature-bullet">▸</span>
                    <span>Automated video posting</span>
                  </div>
                  <div className="feature-preview">
                    <span className="feature-bullet">▸</span>
                    <span>Trending hashtag injection</span>
                  </div>
                  <div className="feature-preview">
                    <span className="feature-bullet">▸</span>
                    <span>Viral timing optimization</span>
                  </div>
                  <div className="feature-preview">
                    <span className="feature-bullet">▸</span>
                    <span>Performance analytics</span>
                  </div>
                </div>
                
                <div className="eta-info">
                  <span className="eta-label">ETA:</span>
                  <span className="eta-value">Q3 2024</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoMode; 